import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AuditLogValueDisplay } from '../../../components/admin/AuditLogValueDisplay';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { useGetAuditLogsQuery } from '../../../store/api/auditLogsApi';
import type { AuditLog } from '../../../types/api';
import { formatDate } from '../../../utils/dateFormatter';
import { getTranslatedAction, getTranslatedTarget, getActionOptionsForTarget, getTargetOptions } from '../../../utils/auditLogTranslations';

export default function AuditLogsListPage() {
  const { t, i18n } = useTranslation();
  const currentLanguage = (i18n.language || 'ar') as 'ar' | 'en';

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [actorId, setActorId] = useState<string>('');
  const [action, setAction] = useState<string>('');
  const [target, setTarget] = useState<string>('');
  const [result, setResult] = useState<'all' | 'success' | 'failed'>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [sortBy, setSortBy] = useState<'createdAt' | 'action' | 'target' | 'result'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading, error } = useGetAuditLogsQuery({
    page,
    limit,
    actorId: actorId ? (actorId === 'system' ? 'system' : parseInt(actorId, 10)) : undefined,
    action: action || undefined,
    target: target || undefined,
    result: result === 'all' ? undefined : result,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    sortBy,
    sortOrder,
  });

  const logs: AuditLog[] = data?.data?.logs || [];
  const pagination = data?.data?.pagination;

  const clearFilters = () => {
    setActorId('');
    setAction('');
    setTarget('');
    setResult('all');
    setStartDate('');
    setEndDate('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setPage(1);
  };

  const getResultColor = (logResult: string) => {
    return logResult === 'success' ? 'text-green-400' : 'text-red-400';
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger">
          {t('admin.auditLogs.error.title') || 'Error loading audit logs'}
        </p>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header */}
      <div
        className="mb-8"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <h1 className="text-3xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.auditLogs.title') || 'سجلات التدقيق'}
        </h1>
        <p className="text-neutral-medium">
          {t('admin.auditLogs.subtitle') || 'عرض جميع سجلات التدقيق في النظام'}
        </p>
      </div>

      {/* Filters */}
      <div
        className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50 mb-6"
        style={{
          animation: 'slideUp 0.5s ease-out 0.1s both',
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
          }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2">
              {t('admin.auditLogs.filters.actorId') || 'معرف المستخدم'}
            </label>
            <input
              type="text"
              value={actorId}
              onChange={(e) => setActorId(e.target.value)}
              placeholder={t('admin.auditLogs.filters.actorIdPlaceholder') || 'Telegram ID أو system'}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium/50 rounded-lg text-neutral-light focus:outline-none focus:border-brand-accent transition-colors duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2">
              {t('admin.auditLogs.filters.target') || 'الهدف'}
            </label>
            <select
              value={target}
              onChange={(e) => {
                setTarget(e.target.value);
                setAction(''); // Reset action when target changes
              }}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium/50 rounded-lg text-neutral-light focus:outline-none focus:border-brand-accent transition-colors duration-200"
            >
              <option value="">{t('admin.auditLogs.filters.all') || 'الكل'}</option>
              {getTargetOptions(t).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2">
              {t('admin.auditLogs.filters.action') || 'الإجراء'}
            </label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              disabled={!target}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium/50 rounded-lg text-neutral-light focus:outline-none focus:border-brand-accent transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">{t('admin.auditLogs.filters.all') || 'الكل'}</option>
              {target && getActionOptionsForTarget(target, t).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2">
              {t('admin.auditLogs.filters.result') || 'النتيجة'}
            </label>
            <select
              value={result}
              onChange={(e) => setResult(e.target.value as 'all' | 'success' | 'failed')}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium/50 rounded-lg text-neutral-light focus:outline-none focus:border-brand-accent transition-colors duration-200"
            >
              <option value="all">{t('admin.auditLogs.filters.all') || 'الكل'}</option>
              <option value="success">{t('admin.auditLogs.filters.success') || 'نجح'}</option>
              <option value="failed">{t('admin.auditLogs.filters.failed') || 'فشل'}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2">
              {t('admin.auditLogs.filters.startDate') || 'تاريخ البداية'}
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium/50 rounded-lg text-neutral-light focus:outline-none focus:border-brand-accent transition-colors duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2">
              {t('admin.auditLogs.filters.endDate') || 'تاريخ النهاية'}
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium/50 rounded-lg text-neutral-light focus:outline-none focus:border-brand-accent transition-colors duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2">
              {t('admin.auditLogs.filters.sortBy') || 'ترتيب حسب'}
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium/50 rounded-lg text-neutral-light focus:outline-none focus:border-brand-accent transition-colors duration-200"
            >
              <option value="createdAt">{t('admin.auditLogs.filters.createdAt') || 'تاريخ الإنشاء'}</option>
              <option value="action">{t('admin.auditLogs.filters.action') || 'الإجراء'}</option>
              <option value="target">{t('admin.auditLogs.filters.target') || 'الهدف'}</option>
              <option value="result">{t('admin.auditLogs.filters.result') || 'النتيجة'}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2">
              {t('admin.auditLogs.filters.sortOrder') || 'ترتيب'}
            </label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium/50 rounded-lg text-neutral-light focus:outline-none focus:border-brand-accent transition-colors duration-200"
            >
              <option value="desc">{t('admin.auditLogs.filters.desc') || 'تنازلي'}</option>
              <option value="asc">{t('admin.auditLogs.filters.asc') || 'تصاعدي'}</option>
            </select>
          </div>
        </form>
        <div className="mt-4 flex gap-2">
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-neutral-medium/20 text-neutral-medium border border-neutral-medium/50 rounded-lg hover:bg-neutral-medium/30 transition-colors duration-200"
          >
            {t('common.clearFilters') || 'مسح الفلاتر'}
          </button>
        </div>
      </div>

      {/* Logs List */}
      <div
        className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
        style={{
          animation: 'slideUp 0.5s ease-out 0.2s both',
        }}
      >
        {logs.length === 0 ? (
          <p className="text-neutral-medium text-center py-8">
            {t('admin.auditLogs.empty') || 'لا توجد سجلات تدقيق'}
          </p>
        ) : (
          <div className="space-y-4">
            {logs.map((log, index) => (
              <div
                key={log._id}
                className="p-4 bg-neutral-dark/50 rounded-lg border border-neutral-medium/50 hover:border-brand-accent/50 transition-all duration-200"
                style={{
                  animation: `fadeIn 0.3s ease-out ${index * 50}ms both`,
                }}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className="text-brand-accent font-semibold">
                        {getTranslatedAction(log.action, t)}
                      </span>
                      <span className="text-neutral-medium">•</span>
                      <span className="text-neutral-light">
                        {getTranslatedTarget(log.target, t)}
                      </span>
                      <span className="text-neutral-medium">•</span>
                      <span className={`font-semibold capitalize ${getResultColor(log.result)}`}>
                        {log.result === 'success'
                          ? t('admin.auditLogs.filters.success') || 'نجح'
                          : t('admin.auditLogs.filters.failed') || 'فشل'}
                      </span>
                    </div>
                    <div className="text-sm text-neutral-medium mb-2">
                      <span className="font-medium">
                        {t('admin.auditLogs.actor') || 'المستخدم'}:{' '}
                      </span>
                      {log.actorId === 'system' ? (
                        <span className="text-brand-accent">System</span>
                      ) : (
                        <Link
                          to={`/admin/users/${log.actorId}`}
                          className="text-brand-accent hover:text-brand-accent/80 transition-colors duration-200"
                        >
                          {log.actorId}
                        </Link>
                      )}
                    </div>
                    <div className="mt-2">
                      <AuditLogValueDisplay log={log} />
                    </div>
                    <div className="mt-2 text-xs text-neutral-medium">
                      {formatDate(log.createdAt, 'PPpp', currentLanguage)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/admin/audit-logs/log/${log._id}`}
                      className="px-3 py-1 text-sm bg-brand-accent/20 text-brand-accent border border-brand-accent/50 rounded-lg hover:bg-brand-accent/30 transition-colors duration-200"
                    >
                      {t('common.view') || 'عرض'}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div
          className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3"
          style={{
            animation: 'fadeIn 0.5s ease-out 0.4s both',
          }}
        >
          <div className="text-neutral-medium text-xs sm:text-sm text-center sm:text-left">
            {t('admin.auditLogs.pagination.showing') || 'عرض'}{' '}
            <span className="text-brand-accent font-semibold">
              {(pagination.page - 1) * pagination.limit + 1}
            </span>{' '}
            -{' '}
            <span className="text-brand-accent font-semibold">
              {Math.min(pagination.page * pagination.limit, pagination.total)}
            </span>{' '}
            {t('admin.auditLogs.pagination.of') || 'من'}{' '}
            <span className="text-brand-accent font-semibold">{pagination.total}</span>{' '}
            {t('admin.auditLogs.pagination.logs') || 'سجل'}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-neutral-dark/50 border border-neutral-medium/50 rounded-lg text-neutral-light hover:bg-neutral-dark transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.previous') || 'السابق'}
            </button>
            <span className="px-4 py-2 bg-neutral-dark/50 border border-neutral-medium/50 rounded-lg text-neutral-light">
              {page} / {pagination.pages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
              disabled={page === pagination.pages}
              className="px-4 py-2 bg-neutral-dark/50 border border-neutral-medium/50 rounded-lg text-neutral-light hover:bg-neutral-dark transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.next') || 'التالي'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

